import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const AnswerType = {
    Text: "Text",
    Date: "Date",
    DateTime: "DateTime",
    Time: "Time",
    Number: "Number",
    Money: "Money",
    Checkbox: "Checkbox",
    MultipleChoice: "Multiple Choice",
    Notes: "Notes",
    Contacts: "Contact(s)",
    Users: "User(s)"
};
export type AnswerTypeType = typeof AnswerType[keyof typeof AnswerType];
class Question extends Base implements EntityType {
    questionId: uuid;
    appliesTo: uuid[];
    position: number;
    question: string;
    answerType: AnswerTypeType;
    answerTypeMulti: boolean;
    choices: string[];
    encrypted: boolean;
    showInCase: boolean;
    private: boolean;
    constructor(doc: typeof Question | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            questionId: genUUID(),
            position: null,
            question: null,
            answerType: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "question";
        // @ts-ignore
        this.questionId = doc._questionId || doc.questionId || doc.questionid || genUUID();
        // @ts-ignore
        this.appliesTo = doc._appliesTo || doc.appliesTo || doc.appliesto || [];
        // @ts-ignore
        this.position = util.coalesce(util.parseInt(doc._position), util.parseInt(doc.position), util.parseInt(doc.position), null);
        // @ts-ignore
        this.question = doc._question || doc.question || null;
        // @ts-ignore
        this.answerType = doc._answerType || doc.answerType || doc.answertype || null;
        // @ts-ignore
        this.answerTypeMulti = util.defaultBoolean([doc.answerTypeMulti, doc._answerTypeMulti, doc.answertypemulti, false]);
        // @ts-ignore
        this.choices = doc._choices || doc.choices || [];
        // @ts-ignore
        this.encrypted = util.defaultBoolean([doc.encrypted, doc._encrypted, doc.encrypted, false]);
        // @ts-ignore
        this.showInCase = util.defaultBoolean([doc.showInCase, doc._showInCase, doc.showincase, false]);
        // @ts-ignore
        this.private = util.defaultBoolean([doc.private, doc._private, doc.private, false]);
    }
    get id(): uuid { return this.questionId; }
    addAppliesTo(appliesTo: uuid): uuid {
        if (!appliesTo) throw new ValidationError('No nulls allowed in an array!');
        if (!this.appliesTo) this.appliesTo = [];
        this.appliesTo.push(appliesTo);
        return appliesTo;
    }
    updateAppliesTo(appliesTo: uuid) {
        if (!this.appliesTo) this.appliesTo = [];
        const index = this.appliesTo.findIndex(item => item === appliesTo);
        if (index !== -1) { this.appliesTo[index] = appliesTo; }
        else this.addAppliesTo(appliesTo);
    }
    removeAppliesTo(appliesTo: uuid) {
        if (!this.appliesTo) return;
        let foundItems = this.appliesTo?.filter(item => item === appliesTo);
        if (foundItems && foundItems.length > 0) {
            this.appliesTo = this.appliesTo.filter(item => !(item === appliesTo));
        }
    }
    abandonAppliesTo() { this.appliesTo = []; }
    static ANSWER_TYPE_VALUES = ["Text", "Date", "DateTime", "Time", "Number", "Money", "Checkbox", "Multiple Choice", "Notes", "Contact(s)", "User(s)"];
    addChoice(choice: string): string {
        if (!choice) throw new ValidationError('No nulls allowed in an array!');
        if (!this.choices) this.choices = [];
        this.choices.push(choice);
        return choice;
    }
    updateChoice(choice: string) {
        if (!this.choices) this.choices = [];
        const index = this.choices.findIndex(item => item === choice);
        if (index !== -1) { this.choices[index] = choice; }
        else this.addChoice(choice);
    }
    removeChoice(choice: string) {
        if (!this.choices) return;
        let foundItems = this.choices?.filter(item => item === choice);
        if (foundItems && foundItems.length > 0) {
            this.choices = this.choices.filter(item => !(item === choice));
        }
    }
    abandonChoices() { this.choices = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this question"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.questionId; }
    static override getNonRefArrayFields(): string[] { return ["appliesTo", "choices"]; }
    static override getDefaultEntries(): Question[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'question',
    table: 'QUESTION',
    comment: 'A user-defined intake question (custom field) that collects structured information during the intake process for contacts, leads, or cases. Each question defines its prompt text, answer type (text, date, number, money, checkbox, multiple-choice, etc.), and optional constraints such as fixed choices, multi-value responses, encryption at rest, and client-portal visibility.',
    fields: {
        questionId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this question'
        },

        /**
         * Used only for lead and matter questions.  Contact questions will leave this empty.
         */
        appliesTo: {
            type: 'uuid[]',
            entityType: 'caseType',
            required: false,
            comment: 'The ID(s) of the related caseType(s). No entry means general question.'
        },

        /**
         * Used with drag-n-drop of questions.
         */
        position: {
            type: 'int',
            required: true,
            comment: 'position in the list (1-n)',
        },

        /**
         * The text of the question.
         */
        question: {
            type: 'string',
            required: true,
            comment: 'The actual question',
        },

        /**
         * The type of the response for the question.
         */
        answerType: {
            type: 'string',
            values: ['Text', 'Date', 'DateTime', 'Time', 'Number', 'Money', 'Checkbox', 'Multiple Choice', 'Notes', 'Contact(s)', 'User(s)'],
            required: true,
            comment: 'What kind of values should be available?',
        },

        /**
         * Do we allow multiple responses? (e.g. multiple people)
         */
        answerTypeMulti: {
            type: 'boolean',
            required: false,
            comment: 'Does this question provide an array of responses?',
        },

        /**
         * Are there fixed choices?
         */
        choices: {
            type: 'string[]', // Stored as strings, but could be anything. We stringify.
            required: false,
            comment: 'Optional list of fixed choices',
        },

        /**
         * Should answers to this question be encrypted in the database?
         */
        encrypted: {
            type: 'boolean',
            required: false,
            comment: 'Should this field be stored encrypted?'
        },

        /**
         * If this is a lead question, should we also show it in the matter (case)?
         */
        showInCase: {
            type: 'boolean',
            comment: `If this is a question for a lead, should it also appear in the case's list?`
        },

        private: {
            type: 'boolean',
            comment: 'Should this question be hidden from clients in the client portal?'
        },
    },
    maps: [
        {name: 'appliesTo', keysFn: t => t.appliesTo, presentFn: (t, k) => t.appliesTo?.includes(k)}
    ]
});
    }
}
export default Question;
